import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { FaPlay, FaStop, FaDesktop } from 'react-icons/fa'; // Import icons
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'; 

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

// Import AG Grid CSS
// import 'ag-grid-community/styles/ag-grid.css';
// import 'ag-grid-community/styles/ag-theme-alpine.css';

const Dashboard = (props) => {
  const [instances, setInstances] = useState([]);

  const fetchInstances = useCallback(async () => {
    const apiEndpoint = 'https://errcj2mc1m.execute-api.us-east-2.amazonaws.com/Production/instances';
    try {
      const response = await axios.get(apiEndpoint, {
        headers: {
          Authorization: `Bearer ${props.auth.user?.id_token}`,
        },
      });
      const body = JSON.parse(response.data.body);
      setInstances(body.instances || []);
    } catch (err) {
        // For any other error, set a generic message
        alert('Failed to fetch instances. Please try again later.');
    }
  }, [props.auth.user?.id_token]);

  useEffect(() => {
    if (props.auth.isAuthenticated && props.auth.user?.id_token) {
      fetchInstances();
    }
  }, [props.auth.isAuthenticated, props.auth.user?.id_token, fetchInstances]);

  const handleInstanceAction = async (instanceId, action) => {
    const actionApiEndpoint = 'https://errcj2mc1m.execute-api.us-east-2.amazonaws.com/Production/instances';
    try {
      await axios.post(actionApiEndpoint, {
        instanceId,
        action
      }, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${props.auth.user?.id_token}`,
        },
      });
      alert(`Action '${action}' initiated for instance ${instanceId}. The grid will refresh shortly.`);
      setTimeout(() => fetchInstances(), 3000); // Refresh data after 3 seconds
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} instance.`);
    }
  };

  // Handler for the new Connect button
  const handleConnectInstance = (instance) => {
    // 1. Define the content of the .rdp file as a string.
    // The most important line is 'full address:s:<IP_ADDRESS>'.
    const rdpContent = [
      `full address:s:${instance.PublicIp}`,
      'prompt for credentials:i:1',
      // 'username:s:Administrator', // Default username for most Windows AMIs
      'screen mode id:i:2',       // Sets the session to full screen
    ].join('\n');

    // 2. Create a 'Blob' which is a file-like object in the browser.
    const blob = new Blob([rdpContent], { type: 'application/rdp' });

    // 3. Create a temporary link element to trigger the download.
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `KVBits_Server.rdp`; // e.g., "My-Server.rdp"

    // 4. Programmatically click the link and then remove it.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 5. Clean up the object URL.
    URL.revokeObjectURL(link.href);
  };

  // Custom component for the action buttons
  const ActionsCellRenderer = (params) => {
    const instance = params.data;
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {instance.State === 'stopped' && (
          <FaPlay
            onClick={() => handleInstanceAction(instance.InstanceId, 'start')}
            style={{ cursor: 'pointer', color: 'green' }}
            title="Start Instance"
          />
        )}
        {instance.State === 'running' && (
          <>
            <FaStop
              onClick={() => handleInstanceAction(instance.InstanceId, 'stop')}
              style={{ cursor: 'pointer', color: 'red', marginRight: '15px' }}
              title="Stop Instance"
            />
            <FaDesktop
              onClick={() => handleConnectInstance(instance)}
              style={{ cursor: 'pointer', color: 'royalblue' }}
              title="Connect (Download RDP)"
            />
          </>
        )}
      </div>
    );
  };

  // Define the columns for AG Grid
  const [columnDefs] = useState([
    { field: 'Name', filter: true, sortable: true, flex: 1 },
    { field: 'InstanceId', headerName: 'Instance ID', filter: true, sortable: true, flex: 2 },
    { field: 'State', sortable: true, flex: 1 },
    { field: 'PublicIp', headerName: 'Public IP', flex: 1 },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      width: 120, // Increased width for two icons
      cellStyle: { textAlign: "center" }
    }
  ]);

  return (
    <div className="dashboard-container">
      <h1>Welcome {props.auth.user?.profile.email}</h1>

      <h2>Servers</h2>
      
      {!props.auth.user.profile['cognito:groups'] ? (
        <div className="error-container" style={{ color: 'red', marginTop: '20px' }}>
          <p>"You are not Authorized to Access the Workstation"</p>
        </div>
      ) : (
        <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
          <AgGridReact
            rowData={instances}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      )} 
    </div>
  );
};

export default Dashboard;